/// <reference types="tsconfig/segment" />
import { EncounterType, TreatmentCatalogue } from '@pm/graphql';
import { trackAnalyticsEvent } from './track-analytics-event';
import { Brand, BrandId } from './types';

type TrackEvent<EventName extends string, Properties extends object> = {
  name: EventName;
  properties: Properties;
};

export type EventMap = {
  [Event in Events as Event['name']]: Event['properties'];
};

export function trackEvent<K extends keyof EventMap>(
  eventName: K,
  properties?: EventMap[K],
) {
  trackAnalyticsEvent(eventName, properties);
}
type Events =
  | AdditionalTreatmentSurveyStarted
  | DashboardTileErrored
  | DashboardViewed
  | TreatmentDetailsViewed
  | AddMedicationClicked
  | AdminViewAsClinician
  | AgreementsAccepted
  | AgreementSkipped
  | AgreementsSignOutClicked
  | AgreementsViewed
  | AppointmentBooked
  | ButtonClicked
  | CalendarAppointmentDetailsOpened
  | CalendarScalesAssessmentReviewClicked
  | CalendarScalesCopied
  | CalendarScalesToggled
  | CalendarViewChanged
  | CalendarViewPatientProfileClicked
  | ConsultCancelled
  | ConsultPreferenceFormSubmitted
  | FlowFinalized
  | FlowLoaded
  | FormFlowEligible
  | FormFlowLoaded
  | FormFlowFinalized
  | FormRedirected
  | FormStepCompleted
  | FormStepLoaded
  | InAppSurveyCompleted
  | LabStepClicked
  | LinkClicked
  | NavLinkClicked
  | NewRxRecordClicked
  | OverrideWorkflowStep
  | PillCountUpdated
  | RemoveMedicationClicked
  | ReprescribeClicked
  | ReviewProfileFormEntered
  | ReviewProfileFormSubmitted
  | ScalesAssessmentReviewClicked
  | ScalesCopied
  | ScalesToggled
  | ShortQuestionnaireStarted
  | StartPostConsultFormClicked
  | StepLoaded
  | StepCompleted
  | SubmitAddressFormEntered
  | SubmitAddressFormSubmitted
  | SubmitFridaPostConsultForm
  | SubmitFridaRxRecord
  | QuestionnaireCompleted
  | QuestionnairePageEntered
  | QuestionnaireStarted
  | ZoomAppointmentClicked
  | USQuestionnaireCompleted
  | FreddieFlowBSignup
  | UnsupportedProvinceLead
  | UpcomingBookingCardViewed
  | UserWaitlisted
  | EncounterTypeSelected;

/**
 * One tile as the patient saw it on a dashboard load. `tileId` and `section`
 * are the dashboard's own constants, so they stay stable as copy changes.
 */
type DashboardTileImpression = {
  tileId: string;
  /** Null for a tile that renders outside every section. */
  section: string | null;
  /** Zero-based position within its section, in on-screen order. */
  position: number;
};

/**
 * One event per dashboard load, describing the whole board. The `tiles` array
 * doubles as the impression denominator for tile click-through, so there is no
 * separate per-tile viewed event.
 */
type DashboardViewed = TrackEvent<
  'Dashboard Viewed',
  {
    tiles: DashboardTileImpression[];
    tileCount: number;
    /** Tiles whose data failed to load and rendered a fallback instead. */
    erroredTileCount: number;
  }
>;

/** A tile's data failed to load, so the patient saw a fallback in its slot. */
type DashboardTileErrored = TrackEvent<
  'Dashboard Tile Errored',
  { tileId: string }
>;

type TreatmentDetailsViewed = TrackEvent<
  'Treatment Details Viewed',
  {
    /** Condition catalogue key, e.g. `hiv_prevention`. */
    condition: string;
    /** Whether the page showed a medication name for the current treatment. */
    hasMedication: boolean;
    pastPrescriptionCount: number;
    hasRenewalDate: boolean;
    /** True when the patient saw the error state rather than the details. */
    hasError: boolean;
  }
>;

type UpcomingBookingCardViewed = TrackEvent<
  'Upcoming Booking Card Viewed',
  {
    location: string;
    currentPath: string;
  }
>;

type AdminViewAsClinicianAttrs<T extends object = object> = T & {
  targetUserId: string;
  adminUserId: string;
};

type AdminViewAsClinician = TrackEvent<
  'Admin View As Clinician',
  AdminViewAsClinicianAttrs
>;

type AgreementsAccepted = TrackEvent<
  'agreementsAccepted',
  { agreementIds: string[]; brandId: BrandId }
>;

type AgreementSkipped = TrackEvent<
  'agreementSkipped',
  { agreementId: string; brandId: BrandId }
>;

type AgreementsSignOutClicked = TrackEvent<
  'agreementsSignOutClicked',
  { brandId: BrandId }
>;

type AgreementsViewed = TrackEvent<
  'agreementsViewed',
  { agreementIds: string[]; brandId: BrandId }
>;

type CalendarAppointmentDetailsOpened = TrackEvent<
  'calendarAppointmentDetailsOpened',
  AppointmentDrawerAttrs
>;

type CalendarViewChanged = TrackEvent<
  'calendarViewChanged',
  {
    selectedView: 'day' | 'week' | 'month';
    previousView: 'day' | 'week' | 'month';
    action: 'selectedFromDropdownList';
  }
>;

type CalendarViewPatientProfileClicked = TrackEvent<
  'calendarViewPatientProfileClicked',
  AppointmentDrawerAttrs
>;

type CalendarScalesToggled = TrackEvent<
  'calendarScalesToggled',
  AppointmentDrawerAttrs<{ open: boolean }>
>;

type CalendarScalesCopied = TrackEvent<
  'calendarScalesCopied',
  AppointmentDrawerAttrs
>;

type CalendarScalesAssessmentReviewClicked = TrackEvent<
  'calendarScalesAssessmentReviewClicked',
  AppointmentDrawerAttrs<{ assessmentId: string }>
>;

type ScalesToggled = TrackEvent<
  'scalesToggled',
  AppointmentDrawerAttrs<{ open: boolean }>
>;

type ScalesCopied = TrackEvent<'scalesCopied', AppointmentDrawerAttrs>;

type ScalesAssessmentReviewClicked = TrackEvent<
  'scalesAssessmentReviewClicked',
  AppointmentDrawerAttrs<{ assessmentId: string }>
>;

type ZoomAppointmentClicked = TrackEvent<
  'zoomAppointmentClicked',
  AppointmentDrawerAttrs<{
    appointmentReason: string | null;
    callType: 'phone' | 'video';
  }>
>;

type StartPostConsultFormClicked = TrackEvent<
  'startPostConsultFormClicked',
  AppointmentDrawerAttrs
>;

type NewRxRecordClicked = TrackEvent<
  'newRxRecordClicked',
  {
    brandId: BrandId;
    patientId: string | null;
  }
>;

type SubmitFridaRxRecord = TrackEvent<'submitFridaRxRecord', PrescriptionAttrs>;

type SubmitFridaPostConsultForm = TrackEvent<
  'submitFridaPostConsultForm',
  PrescriptionAttrs
>;

type AddMedicationClicked = TrackEvent<
  'addMedicationClicked',
  Record<string, never>
>;

type RemoveMedicationClicked = TrackEvent<
  'removeMedicationClicked',
  Record<string, never>
>;

type PillCountUpdated = TrackEvent<'Pill Count Updated', PillCountUpdatedAttrs>;

type AppointmentDrawerAttrs<T extends object = object> = T & {
  brandId: string | null;
  appointmentId: string;
  appointmentType: string | null;
  patientId: string | null;
};

type PrescriptionAttrs<T extends object = object> = T & {
  numberOfPrescriptions: number;
};

type QuestionnaireCompletedAttrs<T extends object = object> = T & {
  definitionId: string | null;
  responseId: string | null;
};

type QuestionnaireCompleted = TrackEvent<
  // Definition name should be used for string
  `${string} Completed`,
  QuestionnaireCompletedAttrs
>;

type QuestionnaireStartedAttrs<T extends object = object> = T & {
  responseId: string | null;
  workflowStepId?: string;
};

type QuestionnaireStarted = TrackEvent<
  // Definition name should be used for string
  `${string} Started`,
  QuestionnaireStartedAttrs
>;

type PillCountUpdatedAttrs<T extends object = object> = T & {
  pillCount: number;
};
type LabStepClickedAttrs<T extends object = object> = T & {
  workflowStepId: string;
};

type LabStepClicked = TrackEvent<`${string} Clicked`, LabStepClickedAttrs>;

type ShortQuestionnaireStartedAttrs<T extends object = object> = T & {
  workflowStepId?: string;
};

type ShortQuestionnaireStarted = TrackEvent<
  'Short Questionnaire Started',
  ShortQuestionnaireStartedAttrs
>;

type QuestionnairePageEnteredAttrs<T extends object = object> = T & {
  pageName?: string;
  questionnaireName?: string;
  brandMembership?: string;
};

type QuestionnairePageEntered = TrackEvent<
  'Questionnaire Page Entered',
  QuestionnairePageEnteredAttrs
>;

type ReviewProfileFormEntered = TrackEvent<
  'Review Profile Form Entered',
  never
>;

type ReviewProfileFormSubmitted = TrackEvent<
  'Review Profile Form Submitted',
  never
>;

type ConsultCancelledAttrs<T extends object = object> = T & {
  appointmentId: string;
  cancellationReason: string;
};

type ConsultCancelled = TrackEvent<'Consult Cancelled', ConsultCancelledAttrs>;

type ConsultPreferenceFormSubmittedAttrs<T extends object = object> = T & {
  consultPreference: 'message' | 'phone' | 'in_person' | 'video';
  postalCode?: string;
};

type ConsultPreferenceFormSubmitted = TrackEvent<
  'Consult Preference Form Submitted',
  ConsultPreferenceFormSubmittedAttrs
>;

type SubmitAddressFormEntered = TrackEvent<
  'Shipping Address Form Entered',
  never
>;

type SubmitAddressFormSubmitted = TrackEvent<
  'Shipping Address Form Submitted',
  never
>;

type OverrideWorkflowStep = TrackEvent<
  'Override Workflow Step',
  { workflowStepId: string; workflowStepType: string; completionParams: object }
>;

type ReprescribeClickedAttrs<T extends object = object> = T & {
  brandId: string;
  prescriptionId: string;
};

type ReprescribeClicked = TrackEvent<
  'Re-prescribe Clicked',
  ReprescribeClickedAttrs
>;

type InAppSurveyCompleted = TrackEvent<
  'In App Survey',
  { question: string; answer: string }
>;

type AdditionalTreatmentSurveyStarted = TrackEvent<
  'Additional Treatment Survey Started',
  { treatment: string }
>;

type CountryCode = string;

type FlowLoaded = TrackEvent<'FlowLoaded', never>;
type FlowFinalized = TrackEvent<'FlowFinalized', never>;
type StepLoaded = TrackEvent<'StepLoaded', { answerLength: number }>;
type StepCompleted = TrackEvent<'StepCompleted', { answerLength: number }>;

type USQuestionnaireCompleted = TrackEvent<'USQuestionnaireCompleted', never>;
type AppointmentBooked = TrackEvent<
  `${CountryCode} Appointment Booked`,
  {
    brandName: string;
    contactType: string;
    countryCode: string;
  }
>;

/**
 * Attributes for link click tracking events
 */
type LinkClickedAttrs<T extends object = object> = T & {
  /** The actual text on the link */
  linkText: string;
  /** An internal name that won't change if text changes. */
  linkName: string;
  /** Target path of the page that will be navigated to.
   * This is considered a constant, and should therefore be snake_case.
   */
  targetPath?: string;
  /** Current path of the page where the button was clicked (window.location.pathname) */
  currentPath: string;
  /** An internal name that won't change if the URL changes. This should be provided
   * any time the current page is known.
   * This is considered a constant, and should therefore be snake_case.
   */
  currentPageName?: string;
  /** Additional context about where the link is located.
   * This can be helpful for cases where there are variations of links on the same page (i.e., A/B tests),
   * or when the current page isn't known or sufficient to identify the link's location.
   * This is considered a constant, and should therefore be snake_case.
   */
  componentContext?: string;
  /** Brand where the event originated from. */
  brandName?: Brand;
};
type LinkClicked = TrackEvent<'Link Clicked', LinkClickedAttrs>;

type NavLinkClickedAttrs<T extends object = object> = T & {
  brandName: Brand;
  targetPath: string;
  currentPath: string;
};
type NavLinkClicked = TrackEvent<'Nav Link Clicked', NavLinkClickedAttrs>;

export type FormName =
  | 'US Intake Questionnaire'
  | 'US Initial Self-Report Questionnaire'
  | 'US Refill Questionnaire'
  | 'US HIV Care Intake Questionnaire'
  | 'HIV Welcome Call Questionnaire'
  | 'General Authed'
  | 'General Inauthed';

type FormFlowLoaded = TrackEvent<`${FormName} Flow Loaded`, FormStepAttrs>;

type FormFlowFinalized = TrackEvent<
  `${FormName} Flow Finalized`,
  FormStepAttrs
>;

type FormFlowEligible = TrackEvent<`${FormName} Eligible`, never>;

type FormStepAttrs<T extends object = object> = T & {
  answerLength: number;
  variantLabel?: string;
  responderUuid?: string;
  flowLabel: string;
  country: string;
  stepId: string;
  stepIndex: number;
  variantRevisionUuid: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
};

type FormStepLoaded = TrackEvent<`${FormName} Step Loaded`, FormStepAttrs>;

export type FormStepCompleted = TrackEvent<
  `${FormName} Step Completed`,
  FormStepAttrs
>;

type FormRedirectedAttrs<T extends object = object> = T & {
  currentPath: string;
  targetPath: string;
};

type FormRedirected = TrackEvent<`${FormName} Redirected`, FormRedirectedAttrs>;

type FreddieFlowBSignup = TrackEvent<
  'Freddie Flow B Signup',
  { patientId: string; howHeardAboutFreddie?: string }
>;

type LeadProps = {
  email: string;
  provinceCode: string;
  brandId: string;
  treatment: TreatmentCatalogue;
};

type UnsupportedProvinceLead = TrackEvent<
  'Freddie Lead - Other Province Dead End',
  LeadProps
>;

type UserWaitlisted = TrackEvent<
  'User Waitlisted',
  Partial<LeadProps> & {
    form: 'Signup Questionnaire' | 'Intake Questionnaire';
    waitlist:
      | 'Pacific Blue Cross'
      | 'Province Waitlist'
      | 'BC Waitlist'
      | 'BC No Private Insurance';
  }
>;

type EncounterTypeSelectedAttrs<T extends object = object> = T & {
  encounterType: EncounterType;
  position: number;
  bookingSessionId?: string;
  choices: { encounterType: EncounterType; eligible: boolean }[];
};

type EncounterTypeSelected = TrackEvent<
  'Encounter Type Selected',
  EncounterTypeSelectedAttrs
>;

/**
 * Attributes for button click tracking events
 */
type ButtonClickedAttrs<T extends object = object> = T & {
  /** The actual text on the button */
  buttonText: string;
  /** An internal name that won't change if text changes.
   * This is considered a constant, and should therefore be snake_case.
   */
  buttonName: string;
  /** Target path of the page that will be navigated to if the button is a link */
  targetPath?: string;
  /** Current path of the page where the button was clicked (window.location.pathname) */
  currentPath: string;
  /** An internal name that won't change if the URL changes. This should be provided
   * any time the current page is known.
   * This is considered a constant, and should therefore be snake_case.
   */
  currentPageName?: string;
  /** Additional context about where the button is located. This can be helpful for
   * cases where there are variations of buttons on the same page (i.e., A/B tests),
   * or when the current page isn't known or sufficient to identify the button's location.
   * This is considered a constant, and should therefore be snake_case.
   */
  componentContext?: string;
};
type ButtonClicked = TrackEvent<'Button Clicked', ButtonClickedAttrs>;
